import { useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Constants, RouteConstants } from "../constants";
import { AuthActionType, useAuthContext } from "../context/AuthContext";
import { LoginFailureReason } from "../models/results/login-result.interface";
import authService from "../services/authentication.service";
import LocalStorageUtil from "../utils/localStorage";

const useLogin = () => {
  const { dispatch } = useAuthContext();

  const [
    loginFailureReason,
    setLoginFailureReason,
  ] = useState<LoginFailureReason | null>(null);

  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const login = useCallback(
    async (email: string, password: string, ssoToken?: string) => {
      try {
        setLoginFailureReason(null);
        setLoading(true);

        const loginRequest = !ssoToken
          ? authService.login(email, password)
          : authService.login("", "", ssoToken);

        const resp = await loginRequest;

        // Handle Failure
        if (!resp?.success || !resp?.data) {
          if (resp?.failReason === LoginFailureReason.PasswordExpired) {
            navigate(RouteConstants.CHANGE_PASSWORD_PATH);
            return;
          }

          setLoginFailureReason(resp.failReason ?? LoginFailureReason.Unknown);
          return;
        }

        const authUser = resp.data;

        // Handle Success
        LocalStorageUtil.set(Constants.USERNAME_KEY, authUser.email);
        LocalStorageUtil.set(Constants.LOGOUT_REASON_KEY, "");

        dispatch({
          type: AuthActionType.SET_USER,
          payload: authUser,
        });

        navigate(from, { replace: true });
      } finally {
        setLoading(false);
      }
    },
    [dispatch, from, navigate]
  );

  return { login, loading, loginFailureReason };
};
export default useLogin;
